import { useTranslation } from 'react-i18next';

// Components
import FavoriteToggleButton from '../../atoms/FavoriteToggleButton/FavoriteToggleButton';
import MenuNavigationButton from '../../molecules/MenuNavigationButton/MenuNavigationButton';
import NavigationAccordionItem from '../../molecules/NavigationAccordionItem/NavigationAccordionItem';
import Picto from '../../atoms/Picto/Picto';
import NavigationMenuPopover from '../NavigationMenuPopover/NavigationMenuPopover';

// Domain
import { getMenuPopoverView, hasMenuChildren } from '../../../domain/menu/menuNavigationUseCase';

// Utils
import cn from '../../../utils/cn';
import { normalizeUrl } from '../../../utils/url';

// Style
import style from './NavigationMenuTree.module.css';

/**
 * NavigationMenuTree component - Sidebar items (L1 accordion) with their sub items (L2).
 * L2 items with children open a mega menu popover; others navigate.
 * With `onFavoriteToggle`, leaves (L2 without children, popover links) get a favorite star on hover.
 * @param {object} params
 * @param {ApiManagerMenuItem[]} params.items - Visible root items
 * @param {boolean} params.isMenuOpen - Whether the sidebar is open
 * @param {string | null} params.expandedItemId - Expanded root item
 * @param {ApiManagerMenuItem | null} params.popoverItem - Sub item whose popover is open
 * @param {number} [params.popoverHiddenCount=0] - Hidden menus of the open popover
 * @param {(item: ApiManagerMenuItem) => void} params.onRootItemClick - Root item click
 * @param {(item: ApiManagerMenuItem) => void} params.onSubItemClick - Sub item click
 * @param {() => void} params.onPopoverClose - Popover dismiss
 * @param {(id: string) => void} params.onLinkClick - Popover link click
 * @param {(itemId: string) => void} params.onManageClick - Popover manage button click
 * @param {(itemId: string) => void} [params.onFavoriteToggle] - Star click (no star when omitted)
 * @returns {JSX.Element} NavigationMenuTree component
 */
function NavigationMenuTree({
  items,
  isMenuOpen,
  expandedItemId,
  popoverItem,
  popoverHiddenCount = 0,
  onRootItemClick,
  onSubItemClick,
  onPopoverClose,
  onLinkClick,
  onManageClick,
  onFavoriteToggle,
}) {
  const { t } = useTranslation();

  /**
   * @param {ApiManagerMenuItem} subItem
   * @returns {React.ReactNode}
   */
  const renderSubItem = (subItem) => {
    const hasChildren = hasMenuChildren(subItem);
    const isOpen = popoverItem?.id === subItem.id;
    const button = (
      <MenuNavigationButton
        key={subItem.id}
        id={`${subItem.id}-sub-button`}
        theme="submenu"
        label={subItem.label}
        href={hasChildren ? undefined : normalizeUrl(subItem.path)}
        isActive={isOpen}
        isMenuOpen={isMenuOpen}
        className={cn([
          style['sub-item'],
          isOpen && style['sub-item-active'],
          !hasChildren && onFavoriteToggle && style['sub-item-with-favorite'],
        ])}
        onClick={() => onSubItemClick(subItem)}
      >
        {/* Side chevron = opens a sub group (popover); no icon = direct link */}
        {hasChildren ? <Picto icon="chevron-right" className={style.icon} /> : null}
      </MenuNavigationButton>
    );

    if (!hasChildren) {
      if (!onFavoriteToggle) return button;

      const isFavorite = !!subItem.config?.favorite;
      return (
        <div key={subItem.id} className={style['sub-item-row']}>
          {button}
          {/* Star in the chevron slot; sibling of the link (no button nested in <a>) */}
          <FavoriteToggleButton
            isFavorite={isFavorite}
            label={t(isFavorite ? 'menu.management.removeFromFavorites' : 'menu.management.addToFavorites')}
            className={cn([style.favorite, isFavorite && style['favorite-on']])}
            onToggle={() => onFavoriteToggle(subItem.id)}
          />
        </div>
      );
    }

    return (
      <NavigationMenuPopover
        key={subItem.id}
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) onPopoverClose();
        }}
        title={subItem.label}
        description={subItem.description}
        settingsDescription={subItem.settingsDescription}
        hiddenCount={isOpen ? popoverHiddenCount : 0}
        view={isOpen ? getMenuPopoverView(subItem) : getMenuPopoverView(null)}
        onLinkClick={onLinkClick}
        onManageClick={() => onManageClick(subItem.id)}
        onFavoriteToggle={onFavoriteToggle}
      >
        {button}
      </NavigationMenuPopover>
    );
  };

  return (
    <div className={style['menu-tree']}>
      {items.map((item) => (
        <NavigationAccordionItem
          key={item.id}
          id={item.id}
          icon={item.icon}
          label={item.label}
          href={hasMenuChildren(item) ? undefined : normalizeUrl(item.path)}
          isExpanded={expandedItemId === item.id}
          isMenuOpen={isMenuOpen}
          onClick={() => onRootItemClick(item)}
        >
          {hasMenuChildren(item) ? item.items.map(renderSubItem) : null}
        </NavigationAccordionItem>
      ))}
    </div>
  );
}

export default NavigationMenuTree;
